/**
 * Session Management Utilities
 * Handles vendor session tracking and credential management
 */

export interface VendorSession {
  vendorNo: string;
  username: string;
  credentialId: string;
  fullName: string;
  email: string;
  loginTime: string;
}

/**
 * Get the current vendor session
 */
export function getVendorSession(): VendorSession | null {
  const session = sessionStorage.getItem('vendorSession');
  if (!session) {
    return null;
  }
  try {
    return JSON.parse(session);
  } catch (e) {
    console.error('Failed to parse vendor session:', e);
    return null;
  }
}

/**
 * Get the current credential ID from the session
 */
export function getCredentialId(): string | null {
  return sessionStorage.getItem('credentialId');
}

/**
 * Check if there is an active vendor session
 */
export function hasActiveSession(): boolean {
  const session = getVendorSession();
  const credentialId = getCredentialId();
  return !!(session && credentialId && session.credentialId === credentialId);
}

/**
 * Clear the vendor session
 * This is typically called on logout or when the session expires
 */
export function clearVendorSession(): void {
  sessionStorage.removeItem('vendorSession');
  sessionStorage.removeItem('credentialId');
  localStorage.removeItem('vendorSession');
}

/**
 * Get session age in milliseconds
 */
export function getSessionAge(): number | null {
  const session = getVendorSession();
  if (!session) {
    return null;
  }
  return Date.now() - new Date(session.loginTime).getTime();
}

/**
 * Check if session is still valid (typically within 2 hours)
 * Session expiration is typically 2 hours from login
 */
export function isSessionValid(): boolean {
  const session = getVendorSession();
  if (!session) {
    return false;
  }
  const sessionAge = getSessionAge();
  if (sessionAge === null) {
    return false;
  }
  // Check if session is older than 2 hours (2 * 60 * 60 * 1000 ms)
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  return sessionAge < TWO_HOURS_MS;
}

/**
 * Get session info for debugging/display
 */
export function getSessionInfo() {
  const session = getVendorSession();
  const credentialId = getCredentialId();
  const sessionAge = getSessionAge();
  const isValid = isSessionValid();

  return {
    hasSession: !!session,
    vendorNo: session?.vendorNo,
    username: session?.username,
    fullName: session?.fullName,
    email: session?.email,
    credentialId,
    credentialIdMatch: session?.credentialId === credentialId,
    loginTime: session?.loginTime,
    sessionAgeMinutes: sessionAge ? Math.floor(sessionAge / 60000) : null,
    isValid,
    message: !session
      ? 'No active session'
      : !isValid
      ? 'Session expired (older than 2 hours)'
      : 'Session active'
  };
}
